import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { Controller, FormProvider, useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import Button from '~/components/buttons/NewButton';
import TextController from '~/components/controllers/TextController/TextController';
import Checkbox from '~/components/inputs/Checkbox/Checkbox';
import DatePicker from '~/components/inputs/DatePicker/DatePicker';
import { HStack, VStack } from '~/components/layout/Stack';
import Text from '~/components/typographies/Text';
import { snack } from '~/hooks/ToastProvider';
import usePrevious from '~/hooks/usePrevious';
import PlusIcon from '~/icons/icon-plus.svg?react';
import { useModal } from '~/modals/Modal';
import { Side, useBookkeepingAccountSuggestion } from '~/services/bookkeeping/account-suggestion';
import { useBookkeepingBankTransaction } from '~/services/bookkeeping/bookkeeping-bank-transactions';
import {
  useBookkeepingTransaction,
  useCreateBookkeepingTransaction,
  useUpdateBookkeepingTransaction,
} from '~/services/bookkeeping/bookkeeping-transactions';
import { useFlowSuspenseQuery } from '~/services/flow';
import {
  useShowAdjustmentColumn,
  useShowCompetencePeriodColumn,
  useShowDescriptionColumn,
} from '~/state/bookkeeping/columnVisibility';
import {
  BookkeepingBankTransaction,
  BookkeepingBankTransactionsKeys,
  BookkeepingEntrySource,
  BookkeepingTransaction,
  TransactionRegistrationProposal,
} from '~/types/general-bookkeeping';
import { BookkeepingTransactionTemplateBody, RegistrationTemplate } from '~/types/registration-template';
import { Transaction } from '~/types/transaction';
import { assertIsDefined, isDefined } from '~/types/typeGuards';
import { requiresAdjustment } from '~/utils/bookkeeping/adjustments';
import { areEntriesBalanced } from '~/utils/bookkeeping/balancing';
import { formEntriesToCreateBookkeepingEntries } from '~/utils/bookkeeping/bookkeeping-entry';
import { getComplementaryInternalTransfer } from '~/utils/bookkeeping/bookkeeping-transaction';
import {
  MAX_REGISTRATION_ASIDE_WIDTH,
  MIN_REGISTRATION_ASIDE_WIDTH,
  useAsideWidth,
} from '~/views/bookkeeping/RegistrationAside/utils/RegistrationAsideState';
import { useBookkeepingTransactionFormDefaultValues } from '~/views/bookkeeping/bank-transactions/bank-transactions-entries';
import tableStyles from '~/views/bookkeeping/registration-table.module.scss';
import BookkeepingTransactionAttachments from './BookkeepingTransactionAttachments';
import styles from './BookkeepingTransactionForm.module.scss';
import BookkeepingTransactionInternalTransferDialog from './BookkeepingTransactionInternalTransferDialog';
import BookkeepingTransactionRecap from './BookkeepingTransactionRecap';
import BookkeepingTransactionRow, { AddRowProps } from './BookkeepingTransactionRow';
import type {
  BookkeepingEntryFormInput,
  BookkeepingTransactionFormInput,
  BookkeepingTransactionFormOutput,
} from './BookkeepingTransactionSchema';
import { BookkeepingTransactionSchema } from './BookkeepingTransactionSchema';

interface Props {
  transactionIds: Transaction['id'][];
  bookkeepingTransactionId: BookkeepingTransaction['id'] | null;
  recommendedReconciliationFlowId: string | null;
  disableSaveAsTemplate?: boolean;
  templateBody?: BookkeepingTransactionTemplateBody | null;
  proposal?: TransactionRegistrationProposal | null;
  onClose: () => void;
  onSuccess: () => void;
  onTemplateCreated?: (template: RegistrationTemplate) => void;
}

type ToFocus = { index: number; focusOn: 'amount' | 'account' } | { index: null; focusOn: 'submit' | null };
const findFocusableIndex = (entries: BookkeepingEntryFormInput[]): ToFocus => {
  if (areEntriesBalanced(entries)) {
    const index = entries.findIndex((entry) => !entry.readOnlyAccount && !entry.bookkeepingAccountId);
    if (index !== -1) {
      return { index, focusOn: 'account' };
    }
    return { index: null, focusOn: 'submit' };
  }
  const index = entries.findIndex((entry) => !entry.readOnlyAmount);
  if (index !== -1) {
    return { index, focusOn: 'amount' };
  }
  return { index: null, focusOn: null };
};

const getRelatedTransactions = (
  transaction: BookkeepingBankTransaction | null,
  bookkeepingTransaction: BookkeepingTransaction | null,
): Transaction[] => {
  if (isDefined(bookkeepingTransaction)) {
    return bookkeepingTransaction.transactions;
  }
  if (isDefined(transaction?.registrationProposal)) {
    return transaction.registrationProposal.transactions;
  }
  if (isDefined(transaction)) {
    return [transaction];
  }

  return [];
};

const BookkeepingTransactionForm: React.FC<Props> = ({
  transactionIds,
  bookkeepingTransactionId,
  recommendedReconciliationFlowId,
  disableSaveAsTemplate = false,
  templateBody,
  proposal = null,
  onClose,
  onSuccess,
  onTemplateCreated,
}) => {
  const { t } = useTranslation('bookkeeping');
  const { showModal } = useModal();
  const queryClient = useQueryClient();
  const { mutateAsync: createTransaction, isPending: isCreatePending } = useCreateBookkeepingTransaction();
  const { mutateAsync: updateTransaction, isPending: isUpdatePending } = useUpdateBookkeepingTransaction();

  const [, setDesiredWidth] = useAsideWidth();

  const [showDescriptionColumn, setShowDescriptionColumn] = useShowDescriptionColumn();
  const [showCompetencePeriodColumn, setShowCompetencePeriodColumn] = useShowCompetencePeriodColumn();
  const [showAdjustmentColumn, setShowAdjustmentColumn] = useShowAdjustmentColumn();

  const setColumnVisibility = useCallback(
    (description: boolean, competencePeriod: boolean, adjustment: boolean) => {
      const maximumAsideWidth = Math.min(window.innerWidth - 200, MAX_REGISTRATION_ASIDE_WIDTH);
      setShowDescriptionColumn(description);
      setShowCompetencePeriodColumn(competencePeriod);
      setShowAdjustmentColumn(adjustment);
      setDesiredWidth(description || competencePeriod || adjustment ? maximumAsideWidth : MIN_REGISTRATION_ASIDE_WIDTH);
    },
    [setShowDescriptionColumn, setShowCompetencePeriodColumn, setShowAdjustmentColumn, setDesiredWidth],
  );

  const previousTemplateBody = usePrevious(templateBody);
  useEffect(() => {
    if (!templateBody || templateBody === previousTemplateBody) return;
    setColumnVisibility(
      templateBody.view.descriptionColumn,
      templateBody.view.competencePeriodColumn,
      showAdjustmentColumn,
    );
  }, [templateBody, previousTemplateBody, showAdjustmentColumn, setColumnVisibility]);

  const { data: transaction } = useBookkeepingBankTransaction(transactionIds[0]);
  const { data: bookkeepingTransaction } = useBookkeepingTransaction(bookkeepingTransactionId);
  const { data: recommendedReconciliationFlow } = useFlowSuspenseQuery(recommendedReconciliationFlowId);

  const defaultValues = useBookkeepingTransactionFormDefaultValues({
    transaction,
    bookkeepingTransaction,
    recommendedReconciliationFlow,
    proposal,
    templateBody,
  });

  const form = useForm<BookkeepingTransactionFormInput, null, BookkeepingTransactionFormOutput>({
    defaultValues,
    resolver: zodResolver(BookkeepingTransactionSchema),
    mode: 'onChange',
  });
  const toFocus = findFocusableIndex(defaultValues.entries);

  const initAdjustmentColumnAutoShowRef = useRef(false);
  useEffect(() => {
    if (initAdjustmentColumnAutoShowRef.current || showAdjustmentColumn) return;
    initAdjustmentColumnAutoShowRef.current = true;
    if (
      defaultValues.entries.some((entry) =>
        requiresAdjustment(defaultValues.date, entry.competencePeriodStart ?? null, entry.competencePeriodEnd ?? null),
      )
    ) {
      setColumnVisibility(showDescriptionColumn, showCompetencePeriodColumn, true);
    }
  }, [defaultValues, showAdjustmentColumn, showDescriptionColumn, showCompetencePeriodColumn, setColumnVisibility]);

  const relatedTransactions = useMemo(
    () => getRelatedTransactions(transaction, bookkeepingTransaction),
    [transaction, bookkeepingTransaction],
  );
  const internalTransferTransaction = useMemo(
    () => getComplementaryInternalTransfer(relatedTransactions, transaction?.id ?? null),
    [relatedTransactions, transaction],
  );

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { isDirty },
  } = form;

  const entriesFieldArray = useFieldArray({
    control,
    name: 'entries',
  });

  const { suggestion, isLoading: isSuggestionLoading } = useBookkeepingAccountSuggestion({
    transactionDescription: defaultValues.description,
  });

  useEffect(() => {
    if (isSuggestionLoading) return;
    if (!isDefined(suggestion)) return;

    const debitSuggestion = suggestion.entries.find((e) => e.side === Side.DEBIT);
    const creditSuggestion = suggestion.entries.find((e) => e.side === Side.CREDIT);

    getValues('entries').forEach((entry, idx) => {
      if (entry.bookkeepingAccountId) return;

      if (isDefined(entry.dare) && isDefined(debitSuggestion)) {
        setValue(`entries.${idx}.bookkeepingAccountId`, debitSuggestion.accountId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }

      if (isDefined(entry.avere) && creditSuggestion) {
        setValue(`entries.${idx}.bookkeepingAccountId`, creditSuggestion.accountId, {
          shouldValidate: true,
          shouldDirty: true,
        });
      }
    });
  }, [suggestion, isSuggestionLoading, setValue, getValues]);

  const executeCreation = useCallback(
    async (values: BookkeepingTransactionFormOutput, transactions: Transaction[]) => {
      assertIsDefined(!bookkeepingTransaction, 'Bookkeeping transaction must not be defined for creation');

      try {
        await createTransaction({
          date: values.date,
          description: values.description,
          bookkeepingEntries: formEntriesToCreateBookkeepingEntries(values.entries, values.date, (type, variant) =>
            t(`transactions.create.form.adjustment_type.${type}.${variant}`),
          ),
          transactionIds: transactions.map((t) => t.id),
          recommendedReconciliationFlowId: values.recommendedReconciliationFlowId,
        });
        snack.success(t('transactions.create.success'));
        onSuccess();
      } catch (error) {
        if (error instanceof Error && error.message.includes('Split Payment VAT account not configured')) {
          snack.error(t('registrations.documents.table.one_click_error_split_payment'));
        } else {
          snack.error(t('transactions.create.error'));
        }
      }
    },
    [createTransaction, t, bookkeepingTransaction, onSuccess],
  );

  const executeUpdate = useCallback(
    async (values: BookkeepingTransactionFormOutput) => {
      assertIsDefined(bookkeepingTransaction, 'Bookkeeping transaction must be defined for update');

      try {
        await updateTransaction({
          bookkeepingTransaction,
          values: {
            date: values.date,
            description: values.description,
            bookkeepingEntries: formEntriesToCreateBookkeepingEntries(values.entries, values.date, (type, variant) =>
              t(`transactions.create.form.adjustment_type.${type}.${variant}`),
            ),
          },
        });
        snack.success(t('transactions.update.success'));
        onSuccess();
      } catch {
        snack.error(t('transactions.update.error'));
      }
    },
    [updateTransaction, t, bookkeepingTransaction, onSuccess],
  );

  const submit = useCallback(
    async (values: BookkeepingTransactionFormOutput) => {
      if (isDefined(bookkeepingTransaction)) {
        await executeUpdate(values);
      } else if (isDirty && isDefined(internalTransferTransaction)) {
        const result = await showModal({
          component: BookkeepingTransactionInternalTransferDialog,
          props: {},
        });
        if (result.action === 'KEEP') {
          await executeCreation(values, relatedTransactions);
        } else if (result.action === 'UNLINK') {
          await executeCreation(values, transaction ? [transaction] : []);
          queryClient.invalidateQueries({
            queryKey: BookkeepingBankTransactionsKeys.registrationProposal(internalTransferTransaction.id),
          });
        }
      } else {
        await executeCreation(values, relatedTransactions);
      }
    },
    [
      queryClient,
      bookkeepingTransaction,
      isDirty,
      showModal,
      transaction,
      relatedTransactions,
      internalTransferTransaction,
      executeCreation,
      executeUpdate,
    ],
  );

  const { append } = entriesFieldArray;
  const isPending = isCreatePending || isUpdatePending;

  const handleAddRow = useCallback(
    (newRow?: AddRowProps): number => {
      append({
        id: null,
        bookkeepingAccountId: newRow?.bookkeepingAccountId ?? '',
        counterpart: null,
        description: '',
        dare: newRow?.dare ?? null,
        avere: newRow?.avere ?? null,
        documentId: null,
        flowId: null,
        documentNumber: null,
        documentType: null,
        source: BookkeepingEntrySource.USER,
        competencePeriodStart: null,
        competencePeriodEnd: null,
        adjustmentAccountId: null,
      });
      return getValues('entries').length - 1;
    },
    [append, getValues],
  );

  const handleDeleteRow = (index: number) => entriesFieldArray.remove(index);

  const handleShowAdjustmentColumn = useCallback(
    () => setColumnVisibility(showDescriptionColumn, showCompetencePeriodColumn, true),
    [setColumnVisibility, showDescriptionColumn, showCompetencePeriodColumn],
  );

  const canDelete = entriesFieldArray.fields.length > 2;
  const hasAttachments = transaction?.attachments?.length || transaction?.notes?.length;
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(submit)} className={styles.form}>
        <VStack className={styles.formContent}>
          <VStack gap={5}>
            <VStack gap={3}>
              <Text variant="subtitle2">{t('transactions.create.form.transaction_details')}</Text>
              <HStack gap={2} className={styles.controls}>
                <TextController
                  label={t('transactions.create.form.description')}
                  name="description"
                  placeholder={t('transactions.create.form.description_placeholder')}
                  control={control}
                  required
                />
                <Controller
                  control={control}
                  name="date"
                  render={({ field, fieldState }) => (
                    <DatePicker
                      {...field}
                      errorMessage={fieldState.error?.message}
                      isInvalid={fieldState.invalid}
                      isRequired
                      label={t('transactions.create.form.date')}
                      value={field.value}
                    />
                  )}
                />
              </HStack>
            </VStack>
            <VStack gap={2} className={styles.entries}>
              {hasAttachments && (
                <BookkeepingTransactionAttachments attachments={transaction.attachments} notes={transaction.notes} />
              )}
              <HStack justify="space-between" align="center">
                <Text variant="subtitle2">{t('transactions.create.form.entries_title')}</Text>
                <HStack gap={2}>
                  <Checkbox
                    checked={showDescriptionColumn}
                    onChange={(isChecked) =>
                      setColumnVisibility(isChecked, showCompetencePeriodColumn, showAdjustmentColumn)
                    }
                  >
                    {t('transactions.create.form.description_column')}
                  </Checkbox>
                  <Checkbox
                    checked={showAdjustmentColumn}
                    onChange={(isChecked) =>
                      setColumnVisibility(showDescriptionColumn, showCompetencePeriodColumn, isChecked)
                    }
                  >
                    {t('transactions.create.form.adjustment_column')}
                  </Checkbox>
                  <Checkbox
                    checked={showCompetencePeriodColumn}
                    onChange={(isChecked) =>
                      setColumnVisibility(showDescriptionColumn, isChecked, showAdjustmentColumn)
                    }
                  >
                    {t('transactions.create.form.competence_period')}
                  </Checkbox>
                </HStack>
              </HStack>
              <HStack className={styles.tableContainer}>
                <table className={tableStyles.table}>
                  <thead>
                    <tr>
                      <th>{t('transactions.create.form.account')}</th>
                      <th className={tableStyles.amount}>{t('transactions.create.form.dare')}</th>
                      <th className={tableStyles.amount}>{t('transactions.create.form.avere')}</th>
                      <th>{t('transactions.create.form.counterpart')}</th>
                      <th>{t('transactions.create.form.document')}</th>
                      {showDescriptionColumn && <th>{t('transactions.create.form.description')}</th>}
                      {showCompetencePeriodColumn && <th>{t('transactions.create.form.competence_period_short')}</th>}
                      {showAdjustmentColumn && (
                        <th className={styles.adjustmentColumn}>
                          {t('transactions.create.form.adjustment_column_short')}
                        </th>
                      )}
                      {canDelete && <th></th>}
                    </tr>
                    <tr className={tableStyles.subheading}>
                      <th />
                      <th />
                      <th />
                      <th />
                      <th />
                      {showDescriptionColumn && <th />}
                      {showCompetencePeriodColumn && <th />}
                      {showAdjustmentColumn && <th className={styles.adjustmentColumn} />}
                      {canDelete && <th></th>}
                    </tr>
                  </thead>
                  <tbody>
                    {entriesFieldArray.fields.map((field, index) => (
                      <BookkeepingTransactionRow
                        key={field.id}
                        autoFocus={toFocus.index === index ? toFocus.focusOn : undefined}
                        index={index}
                        fieldId={field.id}
                        onAddRow={handleAddRow}
                        onDelete={canDelete ? handleDeleteRow : null}
                        onShowAdjustmentColumn={handleShowAdjustmentColumn}
                        transaction={transaction}
                        accountSuggestionIsLoading={isSuggestionLoading}
                      />
                    ))}
                  </tbody>
                </table>
              </HStack>
              <div className={styles.addRow}>
                <Button variant="link" onClick={() => handleAddRow()} Icon={PlusIcon}>
                  {t('transactions.create.form.add_row')}
                </Button>
              </div>
            </VStack>
          </VStack>
          <BookkeepingTransactionRecap
            isUpdate={isDefined(bookkeepingTransactionId)}
            isManual={transactionIds.length === 0 && !isDefined(bookkeepingTransactionId)}
            disableSaveAsTemplate={disableSaveAsTemplate}
            onClose={() => onClose()}
            isSubmitting={isPending}
            autoFocusSubmit={toFocus.focusOn === 'submit'}
            internalTransferTransaction={internalTransferTransaction}
            onTemplateCreated={onTemplateCreated}
          />
        </VStack>
      </form>
    </FormProvider>
  );
};

export default BookkeepingTransactionForm;
